import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { LoadingSpinnerComponent } from '@mlc/shared/loading-spinner/loading-spinner.component';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../../../core/auth/auth.service';
import { HeaderComponent } from '../../../../core/layout/components/header/header.component';
import { Appointment, AppointmentStatus, Case } from '../../../../core/models';
import { AppointmentService } from '../../../../core/services/appointment.service';
import { CaseService } from '../../../../core/services/case.service';
import { MobileDocumentViewerService } from '../../../../core/services/mobile-document-viewer.service';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { MessageBoxService } from '@mlc/shared/message-box/message-box.service';
import { ToastService } from '@mlc/shared/toast-message/toast-service.service';
import { CaseStatusCardComponent } from '../case-status-card/case-status-card.component';
import { CaseStatusDialogService } from '../case-status-dialog/case-status-dialog.service';
import { CaseDetailsNavComponent } from './case-details-nav/case-details-nav.component';

@Component({
  selector: 'app-case-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    CaseDetailsNavComponent,
    TooltipDirective,
    HeaderComponent,
    LoadingSpinnerComponent,
    CaseStatusCardComponent,
  ],
  templateUrl: './case-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaseDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private caseService = inject(CaseService);
  private authService = inject(AuthService);
  private location = inject(Location);
  private destroyRef = inject(DestroyRef);
  private caseStatusDialogService = inject(CaseStatusDialogService);
  private mobileDocumentViewerService = inject(MobileDocumentViewerService);
  private appointmentService = inject(AppointmentService);
  private messageBoxService = inject(MessageBoxService);
  private toastService = inject(ToastService);

  isMobileDocumentViewerOpen = this.mobileDocumentViewerService.isMobileDocumentViewerOpen;

  patientKey = signal<string | null>(null);
  doctorName = signal<string | null>(null);
  activeAppointment = signal<Appointment | null>(null);
  isActionLoading = signal<boolean>(false);
  private origin: string | null = null;
  private currentOrgCode: string | null = null;

  isLoadingData = computed(() => this.caseService.currentCaseDetails === null);

  canMarkAsSeen = computed(() => {
    const appt = this.activeAppointment();
    if (!appt) return false;
    const isEligibleDate = this.appointmentService.isEligibleForCompletion(appt.start);
    const isEligibleStatus =
      appt.statusId === AppointmentStatus.SCHEDULED || appt.statusId === AppointmentStatus.CHECKED_IN;
    return isEligibleDate && isEligibleStatus;
  });

  isSeen = computed(() => {
    return this.activeAppointment()?.statusId === AppointmentStatus.COMPLETED;
  });

  canUndoSeen = computed(() => {
    const appt = this.activeAppointment();
    if (!appt || appt.statusId !== AppointmentStatus.COMPLETED) return false;
    return this.appointmentService.isEligibleForSameDayUndo(appt.updateTime);
  });

  caseDetails$: Observable<Case | null> = this.caseService.caseDetails$;


  ngOnInit(): void {
    this.authService.getLoggedInUser().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      if (user.firstName && user.lastName) {
        this.doctorName.set(`${user.firstName} ${user.lastName}`);
      } else if (user.displayName) {
        this.doctorName.set(user.displayName);
      } else {
        this.doctorName.set(null);
      }
    });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.origin = params.get('origin');
    });

    this.route.paramMap
      .pipe(
        switchMap(paramMap => {
          const patientKey = paramMap.get('patientKey');
          return this.route.queryParamMap.pipe(
            map(queryParamMap => ({ patientKey, orgCode: queryParamMap.get('orgCode') }))
          );
        }),
        filter(({ patientKey }) => !!patientKey),
        tap(({ patientKey, orgCode }) => {
          this.patientKey.set(patientKey);
          this.currentOrgCode = orgCode;
          if (patientKey) {
            this.fetchAppointments(patientKey);
          }
          if (patientKey && orgCode) {
            this.fetchCaseDetails(patientKey, orgCode, true);
          } else if (patientKey) {
            this.fetchCaseDetails(patientKey, 'defaultOrgCode', true);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.caseService.caseDetails$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(details => {
      console.log('CaseDetailsComponent: caseDetails updated:', details);
    });
  }

  fetchAppointments(patientKey: string): void {
    this.appointmentService
      .getAppointmentsByPatient(patientKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (appointments) => {
          const eligibleAppointments = appointments.filter((a) =>
            this.appointmentService.isEligibleForCompletion(a.start)
          );
          const pendingAppt = eligibleAppointments.find(
            (a) => a.statusId === AppointmentStatus.SCHEDULED || a.statusId === AppointmentStatus.CHECKED_IN
          );
          const completedAppt = eligibleAppointments.find(
            (a) => a.statusId === AppointmentStatus.COMPLETED
          );
          this.activeAppointment.set(pendingAppt || completedAppt || eligibleAppointments[0] || null);
        },
        error: (err) => console.error('[CaseDetailsComponent] Error fetching appointments:', err),
      });
  }

  async onMarkAsSeen(): Promise<void> {
    const appt = this.activeAppointment();
    const patientKey = this.patientKey();
    const orgCode = this.currentOrgCode || appt?.orgCode;

    if (!appt || !patientKey || !orgCode) {
      this.toastService.error('Missing appointment information.');
      return;
    }

    const patientName =
      this.caseService.currentCaseDetails?.name ||
      `${this.caseService.currentCaseDetails?.firstName || ''} ${this.caseService.currentCaseDetails?.lastName || ''}`.trim() ||
      'the patient';

    const confirmed = await this.messageBoxService.open({
      title: 'Confirm Patient Evaluation',
      message: `Are you sure you want to mark the appointment for ${patientName} as Seen? This will notify clinic staff that the clinical evaluation is complete.`,
      confirmText: 'Mark as Seen',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    this.isActionLoading.set(true);
    try {
      const response = await this.appointmentService.markAppointmentComplete(orgCode, patientKey, appt.id);
      this.toastService.success('Appointment marked as Seen successfully!');
      this.activeAppointment.set({
        ...appt,
        statusId: AppointmentStatus.COMPLETED,
        updateTime: response.updateTime ? new Date(response.updateTime) : new Date(),
      });
    } catch (error: any) {
      console.error('[CaseDetailsComponent] Error marking appointment as seen:', error);
      this.toastService.error(error?.message || 'Failed to mark appointment as Seen.');
    } finally {
      this.isActionLoading.set(false);
    }
  }

  async onUndoSeen(): Promise<void> {
    const appt = this.activeAppointment();
    const patientKey = this.patientKey();
    const orgCode = this.currentOrgCode || appt?.orgCode;

    if (!appt || !patientKey || !orgCode) {
      this.toastService.error('Missing appointment information.');
      return;
    }

    const confirmed = await this.messageBoxService.open({
      title: 'Undo Appointment Completion',
      message:
        'Are you sure you want to undo marking this appointment as Seen? The appointment will be restored to its previous status.',
      confirmText: 'Yes, Undo',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    this.isActionLoading.set(true);
    try {
      const response = await this.appointmentService.undoAppointmentComplete(orgCode, patientKey, appt.id);
      this.toastService.success('Appointment status reverted successfully.');
      this.activeAppointment.set({
        ...appt,
        statusId: response.statusId,
        updateTime: response.updateTime ? new Date(response.updateTime) : new Date(),
      });
    } catch (error: any) {
      console.error('[CaseDetailsComponent] Error undoing appointment completion:', error);
      this.toastService.error(error?.message || 'Failed to undo appointment completion.');
    } finally {
      this.isActionLoading.set(false);
    }
  }

  fetchCaseDetails(patientKey: string, orgCode: string, forceCloudFunctionCall: boolean = false): void {
    this.caseService.clearCaseDetails(); // Always clear details to show loading state

    this.authService.getLoggedInUser()
      .pipe(
        filter(user => !!user && !!user.uid),
        switchMap(user => {
          return this.caseService.getPatientDetails(orgCode, patientKey, user!.uid, forceCloudFunctionCall);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: (err) => console.error('Error fetching case details:', err)
      });
  }

  refreshCaseDetails(): void {
    if (this.patientKey() && this.currentOrgCode) {
      this.fetchCaseDetails(this.patientKey()!, this.currentOrgCode, true);
      console.log('Refreshing case details for', this.patientKey());
    } else {
      console.warn('Cannot refresh case details: patientKey or orgCode is missing.');
    }
  }

  closeCaseDetails(): void {
    const navigatePath = this.origin ? `/app/${this.origin}` : '/app/dashboard';
    this.router.navigate([navigatePath]);
  }

  openCaseStatusDialog(): void {
    this.caseStatusDialogService.open(this.caseDetails$);
  }

  calculateAge(dob: any): number {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  genderDisplay(gender: any): string {
    if (!gender) return 'N/A';

    const g = gender.toString().trim().toLowerCase();

    if (g === 'male') return 'M';
    if (g === 'female') return 'F';
    if (g === 'other') return 'Other';

    return 'N/A';
  }

  formatCaseInfo(details: Case): string {
    if (!details) {
      return '';
    }

    let caseInfo = details.caseType || '';

    if (details.caseCategory) {
      caseInfo += ` - ${details.caseCategory}`;
    }

    if (details.org?.name) {
      caseInfo += ` | ${details.org.name}`;
    }

    return caseInfo;
  }

  formatPatientSubheader(details: Case): string {
    if (!details) {
      return '';
    }

    const age = details.dob ? this.calculateAge(details.dob) : null;
    const gender = details.gender ? this.genderDisplay(details.gender) : null;

    if (age && gender && gender !== 'N/A') {
      return `(${age}/${gender})`;
    } else if (age) {
      return `(${age})`;
    } else if (gender && gender !== 'N/A') {
      return `(${gender})`;
    }

    return '';
  }
}
